import type {
  ProductCategory,
  ProductKind,
  ProductType,
} from '@iheartjane/types';

import type { BrandSpecialUserSegmentIds } from './brandSpecials';

/** Taken from `api/app/models/cart_limit_rule.rb` */
export type ProductUnits = 'fl_oz' | 'g' | 'mg' | 'ml' | 'oz';

export interface Effect {
  id: number;
  title: string;
  value: string;
}

export type FlavorEnum =
  | 'citrusy'
  | 'spicy'
  | 'floral'
  | 'hoppy'
  | 'earthy'
  | 'herbal';

export const TerpeneEnumValues = [
  'limonene',
  'nerolidol',
  'valencene',
  'caryophyllene',
  'caryophyllene oxide',
  'sabinene',
  'linalool',
  'geraniol',
  'geranyl acetate',
  'humulene',
  'myrcene',
  'pinene',
  'camphene',
  'camphor',
  'carene',
  'cedrene',
  'guaiol',
  'eucalyptol',
  'terpineol',
  'terpinolene',
  'phellandrene',
  'bisabolol',
  'borneol',
  'cymene',
  'fenchol',
  'menthol',
  'isoborneol',
  'isopulegol',
  'pulegone',
  'ocimene',
  'phytol',
] as const;

export type TerpeneEnum = (typeof TerpeneEnumValues)[number];

export interface Flavor {
  id: number;
  title: string;
  value: string;
}

interface Alias {
  id: number;
  name: string;
}

export interface BusinessLicense {
  business_name: string;
  id: number;
  license_number: string;
  state: string;
}

export interface Photo {
  id: number;
  position: number;
  urls: {
    extraLarge: string;
    medium: string;
    original: string;
    small: string;
  };
}

interface LocalizedContent {
  brand_subtype?: string | null;
  country?: string | null;
  description?: string | null;
  image_urls?: string[];
  lineage?: ProductCategory | null;
  name?: string | null;
  photos?: Photo[];
  subdivision?: string | null;
}

interface BrandPriceIdSpecials {
  brand_special_id: number;
  discount_label: string;
  price: string;
}

export interface BrandSpecialPrices {
  each?: BrandPriceIdSpecials | null;
  eighth_ounce?: BrandPriceIdSpecials | null;
  gram?: BrandPriceIdSpecials | null;
  half_gram?: BrandPriceIdSpecials | null;
  half_ounce?: BrandPriceIdSpecials | null;
  ounce?: BrandPriceIdSpecials | null;
  quarter_ounce?: BrandPriceIdSpecials | null;
  two_gram?: BrandPriceIdSpecials | null;
}

interface ProductOptionalProperties {
  aggregate_rating?: number | null;
  aliases?: Alias[];
  allergens?: string[];
  amount?: string | null;
  applicable_brand_special_ids?: number[];
  applicable_brand_specials_excluded_user_segments?: BrandSpecialUserSegmentIds;
  applicable_brand_specials_included_user_segments?: BrandSpecialUserSegmentIds;
  brand_special_prices?: BrandSpecialPrices | null;
  brand_subtype?: string | null;
  business_licenses?: BusinessLicense[] | null;
  category?: ProductCategory | null;
  cbd_dosage_milligrams?: number | null;
  description?: string;
  dosage?: string | null;
  effects?: Effect[];
  flavors?: Flavor[];
  has_brand_discount?: boolean | null;
  image_urls?: string[];
  ingredients?: string[];
  localized_content?: LocalizedContent[];
  lowest_available_price?: {
    found: true;
    price?: number | null;
    val?: string;
  };
  net_weight_grams?: number | null;
  percent_cbd?: number | null;
  percent_cbda?: number | null;
  percent_tac?: number | null;
  percent_thc?: number | null;
  percent_thca?: number | null;
  percentCBDA?: number | null;
  percentTHCA?: number | null;
  photos?: Photo[];
  primaryPercentCBD?: number;
  primaryPercentTHC?: number;
  quantity_units?: ProductUnits | null;
  quantity_value?: number | null;
  recommended?: boolean;
  review_count?: number | null;
  root_subtype?: string | null;
  terpenes?: Record<string, string>[];
  thc_dosage_milligrams?: number | null;
  url_slug?: string;
}

export type Product = {
  brand: string | null;
  id: number;
  kind: ProductKind;
  name: string;
  product_brand_id?: number | null;
  type: ProductType;
} & ProductOptionalProperties;
